import { useEffect, useState } from 'react';
import { isRemoteControlExcluded } from '@srm/screen-sharing/light';
import { useLatest } from '@srm/ui/hooks/useLatest';

/** Ввод пользователя, который глушится, пока кабинетом управляет сотрудник. */
export const BLOCKED_INPUT_EVENTS = [
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'dblclick',
    'auxclick',
    'contextmenu',
    'touchstart',
    'touchmove',
    'touchend',
    'touchcancel',
    'keydown',
    'keypress',
    'keyup',
    'wheel',
    // Ввод мимо клавиатурных событий: IME, вставка, вырезание, перетаскивание. `submit` не глушим: браузер шлёт его
    // доверенным и на команду сотрудника (клик по кнопке отправки, requestSubmit), а свой submit пользователь
    // без заглушённых клика и Enter не вызовет.
    'beforeinput',
    'input',
    'compositionstart',
    'compositionupdate',
    'compositionend',
    'paste',
    'cut',
    'dragstart',
    'drop',
] as const;
/** Подсказка у курсора появляется не чаще… */
export const INPUT_HINT_THROTTLE = 1500;
/** …и держится столько. */
export const INPUT_HINT_DURATION = 2500;

export interface IRemoteControlHint {
    x: number;
    y: number;
    text: string;
}

interface IOptions {
    /** Управление выдано или выдаётся (пользователь нажал «Разрешить»): глушим ввод */
    active: boolean;
    /** Кто управляет — для подсказки */
    peerName: string;
    /** Пользователь нажал Escape — забрать управление */
    onRelease: () => void;
}

/**
 * Ввод самого пользователя — доверенное событие браузера. Объект, а не функция: jsdom не даёт подделать
 * `isTrusted`, тесты подменяют проверку.
 */
export const userInput = {
    is: (event: Event): boolean => event.isTrusted,
};

/**
 * Пока сотрудник управляет кабинетом, ввод пользователя не доходит до страницы: слушатели на `window` в фазе
 * перехвата отменяют доверенные (`isTrusted`) события вне исключённых областей (`data-remote-control-exclude`,
 * класс `remote-control-exclude` — полоса поддержки, меню помощи, окна подписи) — они остаются рабочими. Команды сотрудника — синтетические события (`isTrusted = false`) и проходят. Escape всегда
 * забирает управление. Возвращает подсказку «Сейчас управляет…» у курсора после попытки ввода.
 */
export function useRemoteControlInputBlock({ active, peerName, onRelease }: IOptions): IRemoteControlHint | null {
    const [hint, setHint] = useState<IRemoteControlHint | null>(null);
    const release = useLatest(onRelease);

    useEffect(() => {
        if (!active) return undefined;
        const text = `Сейчас управляет ${peerName} — Esc, чтобы забрать управление`;
        let pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        let shownAt = Number.NEGATIVE_INFINITY;
        let hideTimer: ReturnType<typeof setTimeout> | undefined;
        const show = () => {
            const now = Date.now();
            if (now - shownAt < INPUT_HINT_THROTTLE) return;
            shownAt = now;
            setHint({ ...pointer, text });
            clearTimeout(hideTimer);
            hideTimer = setTimeout(() => setHint(null), INPUT_HINT_DURATION);
        };
        const track = (event: PointerEvent) => {
            if (userInput.is(event)) pointer = { x: event.clientX, y: event.clientY };
        };
        const block = (event: Event) => {
            if (!userInput.is(event)) return;
            if (event instanceof KeyboardEvent && event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                if (event.type === 'keydown') release.current();
                return;
            }
            if (isRemoteControlExcluded(event.target)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            // IME и диктовка не отменяются preventDefault: поле теряет фокус, и набор не доходит до страницы.
            if (event.type === 'compositionstart' && document.activeElement instanceof HTMLElement)
                document.activeElement.blur();
            if (event instanceof MouseEvent) pointer = { x: event.clientX, y: event.clientY };
            show();
        };
        window.addEventListener('pointermove', track, { capture: true, passive: true });
        BLOCKED_INPUT_EVENTS.forEach((type) => window.addEventListener(type, block, { capture: true, passive: false }));
        return () => {
            window.removeEventListener('pointermove', track, { capture: true });
            BLOCKED_INPUT_EVENTS.forEach((type) => window.removeEventListener(type, block, { capture: true }));
            clearTimeout(hideTimer);
            setHint(null);
        };
    }, [active, peerName, release]);

    return active ? hint : null;
}
